import type {
  AnnualRevenue,
  ProductDescription,
} from "../../onboarding/OnboardingShopInfoContent";

import * as React from "react";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { ArrowRight } from "lucide-react";

import OnboardingShopInfoContent from "../../onboarding/OnboardingShopInfoContent";

interface ShopInfoProps {
  onContinue?: (info: {
    name: string;
    subdomain: string;
    productDescription: ProductDescription;
    annualRevenue: AnnualRevenue;
  }) => void;
  onClose?: () => void;
}

export default function ShopInfo({ onContinue, onClose }: ShopInfoProps) {
  const [shopName, setShopName] = React.useState<string>("");
  const [subdomain, setSubdomain] = React.useState<string>("");
  const [productDescription, setProductDescription] =
    React.useState<ProductDescription | null>(null);
  const [annualRevenue, setAnnualRevenue] =
    React.useState<AnnualRevenue | null>(null);

  React.useEffect(() => {
    if (shopName) {
      const formatted = shopName
        .toLowerCase()
        .replace(/[^\d\sa-z-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      setSubdomain(formatted);
    } else {
      setSubdomain("");
    }
  }, [shopName]);

  const isNextDisabled =
    shopName.trim() === "" ||
    subdomain.trim() === "" ||
    productDescription === null ||
    annualRevenue === null;

  const handleContinue = () => {
    if (!isNextDisabled && productDescription && annualRevenue) {
      onContinue?.({
        name: shopName.trim(),
        subdomain: subdomain.trim(),
        productDescription,
        annualRevenue,
      });
    }
  };

  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open) {
          onClose?.();
        }
      }}
      size="custom"
      title="Tell us about your shop"
      description="We'll use this info to tailor recommendations for you."
      layoutClassName="h-[900px] w-[1100px]"
      UNSAFE_className="[&_.header-h2]:text-center [&_.header-h2]:text-3xl [&_.header-h2]:py-4 [&_p.text-muted]:text-center"
      footer={
        <div className="w-full">
          <div className="border-t-[0.5px] border-border -mx-6" />
          <div className="flex justify-between items-center w-full pt-4">
            <div className="text-sm text-muted flex items-center h-10 leading-none">
              Step 1 of 3
            </div>
            <Button
              variant="primary"
              size="base"
              onClick={handleContinue}
              disabled={isNextDisabled}
              endEnhancer={<ArrowRight size={16} />}
            >
              Continue
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex items-start justify-center min-h-0 flex-1 overflow-y-auto py-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="lg:w-[1000px] w-full px-5 lg:px-0">
          <OnboardingShopInfoContent
            shopName={shopName}
            setShopName={setShopName}
            subdomain={subdomain}
            setSubdomain={setSubdomain}
            productDescription={productDescription}
            setProductDescription={setProductDescription}
            annualRevenue={annualRevenue}
            setAnnualRevenue={setAnnualRevenue}
            isBusinessContext={true}
          />
        </div>
      </div>
    </Modal>
  );
}
